const FREQUENTLY_ADDED_STORAGE_KEY = 'cartDrawerFrequentlyAddedRemoved';

function readFrequentlyAddedRemovedKeys() {
  try {
    return JSON.parse(sessionStorage.getItem(FREQUENTLY_ADDED_STORAGE_KEY) || '[]');
  } catch {
    return [];
  }
}

function writeFrequentlyAddedRemovedKeys(keys) {
  sessionStorage.setItem(FREQUENTLY_ADDED_STORAGE_KEY, JSON.stringify(keys));
}

function setDrawerAddButtonLoading(button, isLoading) {
  if (!button) return;
  const spinner = button.querySelector('.loading__spinner');
  button.classList.toggle('loading', isLoading);
  if (spinner) spinner.classList.toggle('hidden', !isLoading);
}

function setCompleteSetupCheckLoading(checkbox, isLoading) {
  if (!checkbox) return;
  const wrap = checkbox.closest('.drawer__complete-setup-check-wrap');
  if (!wrap) return;
  const spinner = wrap.querySelector('.loading__spinner');
  wrap.classList.toggle('is-loading', isLoading);
  if (spinner) spinner.classList.toggle('hidden', !isLoading);
}

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', this.onBundleSavingsClick.bind(this));
    this.addEventListener('click', this.onFrequentlyAddedClick.bind(this));
    this.addEventListener('change', this.onFrequentlyAddedChange.bind(this));
    this.addEventListener('focusin', this.onFrequentlyAddedFocusIn.bind(this));
    this.addEventListener('click', this.onCompleteSetupClick.bind(this));
    this.addEventListener('change', this.onCompleteSetupChange.bind(this));
    this.addEventListener('focusin', this.onCompleteSetupFocusIn.bind(this));
    this.purgeFrequentlyAddedFromStorage();
    this.setHeaderCartIconAccessibility();
  }

  /**
   * Replace cart line (product with bundle_savings metafield) with the bundle variant from the metaobject.
   */
  onBundleSavingsClick(event) {
    const button = event.target.closest('[data-bundle-savings-add]');
    if (!button || button.getAttribute('aria-disabled') === 'true') return;

    event.preventDefault();
    const line = parseInt(button.dataset.cartLine, 10);
    const variantId = parseInt(button.dataset.bundleVariantId, 10);
    const quantity = Math.max(1, parseInt(button.dataset.bundleQuantity, 10) || 1);

    if (!line || !variantId) return;

    button.setAttribute('aria-disabled', 'true');
    setDrawerAddButtonLoading(button, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;

    const bodyRemove = JSON.stringify({
      line,
      quantity: 0,
      sections,
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body: bodyRemove })
      .then((response) => response.text())
      .then((text) => JSON.parse(text))
      .then((changeState) => {
        if (changeState.errors) {
          const msg = typeof changeState.errors === 'string' ? changeState.errors : changeState.errors.join?.(', ') || '';
          throw new Error(msg || 'cart change');
        }

        const bodyAdd = JSON.stringify({
          items: [{ id: variantId, quantity }],
          sections: sections.join(','),
          sections_url: sectionsUrl,
        });

        return fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd }).then((r) => r.json());
      })
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'bundle-savings',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setDrawerAddButtonLoading(button, false);
        button.removeAttribute('aria-disabled');
      });
  }

  purgeFrequentlyAddedFromStorage() {
    const removed = readFrequentlyAddedRemovedKeys();
    if (!removed.length) {
      this.updateFrequentlyAddedSectionVisibility();
      return;
    }
    this.querySelectorAll('[data-frequently-added-card]').forEach((card) => {
      const key = card.dataset.rowKey;
      if (key && removed.includes(key)) {
        card.remove();
      }
    });
    this.updateFrequentlyAddedSectionVisibility();
  }

  updateFrequentlyAddedSectionVisibility() {
    const section = this.querySelector('[data-frequently-added-section]');
    if (!section) return;
    const items = section.querySelector('[data-frequently-added-items]');
    if (!items || items.children.length === 0) {
      section.classList.add('hidden');
    } else {
      section.classList.remove('hidden');
    }
  }

  frequentlyAddedParseJson(card) {
    const el = card.querySelector('.js-frequently-added-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  frequentlyAddedUpdatePriceDisplay(card) {
    const data = this.frequentlyAddedParseJson(card);
    if (!data) return;

    const variantSel = card.querySelector('[data-variant-select]');
    const variantId = variantSel ? variantSel.value : String(data.variants[0]?.id);
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    const mode = purchaseVisible ? purchase.dataset.purchaseMode || 'onetime' : 'onetime';

    let priceFormatted = v.price_formatted;
    let compareFormatted = v.compare_at_formatted || '';

    const plans = v.selling_plans || [];
    if (mode === 'subscribe' && planSel && !planSel.disabled && plans.length > 0 && planSel.value) {
      const plan = plans.find((p) => String(p.id) === String(planSel.value));
      if (plan && plan.price_formatted) {
        priceFormatted = plan.price_formatted;
        compareFormatted = plan.compare_at_formatted || '';
      }
    }

    const priceEl = card.querySelector('[data-frequently-added-price]');
    const compareEl = card.querySelector('[data-frequently-added-compare]');
    if (priceEl) priceEl.textContent = priceFormatted;
    if (compareEl) {
      if (compareFormatted) {
        compareEl.textContent = compareFormatted;
        compareEl.classList.remove('hidden');
      } else {
        compareEl.textContent = '';
        compareEl.classList.add('hidden');
      }
    }
  }

  setFrequentlyAddedPurchaseMode(card, mode) {
    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const onetimeBtn = card.querySelector('[data-purchase-type="onetime"]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    const wrap = card.querySelector('.drawer__frequently-added-subscribe-wrap');
    if (!purchase || !onetimeBtn || !planSel) {
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    purchase.dataset.purchaseMode = mode;

    if (mode === 'onetime') {
      onetimeBtn.classList.add('drawer__frequently-added-pill--active');
      onetimeBtn.setAttribute('aria-pressed', 'true');
      wrap?.classList.remove('drawer__frequently-added-subscribe-wrap--active');
      planSel.disabled = true;
    } else {
      onetimeBtn.classList.remove('drawer__frequently-added-pill--active');
      onetimeBtn.setAttribute('aria-pressed', 'false');
      wrap?.classList.add('drawer__frequently-added-subscribe-wrap--active');
      planSel.disabled = false;
    }

    this.frequentlyAddedUpdatePriceDisplay(card);
  }

  frequentlyAddedApplyVariant(card, variantId) {
    const data = this.frequentlyAddedParseJson(card);
    if (!data) return;
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const imgEl = card.querySelector('[data-frequently-added-img]');
    if (imgEl) {
      if (v.featured_media_url) {
        if (imgEl.tagName === 'IMG') {
          imgEl.src = v.featured_media_url;
          imgEl.classList.remove('hidden');
        }
      } else if (imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('src');
        imgEl.classList.add('hidden');
      }
    }

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    if (!purchase || !planSel) {
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    const plans = v.selling_plans || [];
    if (plans.length === 0) {
      purchase.hidden = true;
      purchase.classList.add('hidden');
      const addBtnOnly = card.querySelector('[data-frequently-added-add]');
      if (addBtnOnly) addBtnOnly.disabled = !v.available;
      this.frequentlyAddedUpdatePriceDisplay(card);
      return;
    }

    purchase.hidden = false;
    purchase.classList.remove('hidden');
    planSel.innerHTML = '';
    plans.forEach((p) => {
      const o = document.createElement('option');
      o.value = String(p.id);
      o.textContent = p.name;
      planSel.appendChild(o);
    });

    let matchDefault = null;
    if (data.defaultSellingPlanId != null) {
      matchDefault = plans.find((p) => String(p.id) === String(data.defaultSellingPlanId));
    }
    const pick = matchDefault || plans[0];
    planSel.value = String(pick.id);

    if (matchDefault) {
      this.setFrequentlyAddedPurchaseMode(card, 'subscribe');
    } else {
      this.setFrequentlyAddedPurchaseMode(card, 'onetime');
    }

    const addBtn = card.querySelector('[data-frequently-added-add]');
    if (addBtn) addBtn.disabled = !v.available;

    this.frequentlyAddedUpdatePriceDisplay(card);
  }

  onFrequentlyAddedFocusIn(event) {
    if (event.target.closest('[data-complete-setup-card]')) return;
    const planSel = event.target.closest('[data-selling-plan-select]');
    if (!planSel || !this.contains(planSel)) return;
    const card = planSel.closest('[data-frequently-added-card]');
    if (!card) return;
    this.setFrequentlyAddedPurchaseMode(card, 'subscribe');
  }

  onFrequentlyAddedClick(event) {
    if (event.target.closest('[data-complete-setup-card]')) return;
    const onetimeBtn = event.target.closest('[data-purchase-type="onetime"]');
    if (onetimeBtn && this.contains(onetimeBtn)) {
      const card = onetimeBtn.closest('[data-frequently-added-card]');
      if (card) this.setFrequentlyAddedPurchaseMode(card, 'onetime');
      return;
    }

    const subscribeWrap = event.target.closest('.drawer__frequently-added-subscribe-wrap');
    if (subscribeWrap && this.contains(subscribeWrap)) {
      const planSel = subscribeWrap.querySelector('[data-selling-plan-select]');
      if (planSel?.disabled) {
        event.preventDefault();
        const card = subscribeWrap.closest('[data-frequently-added-card]');
        if (card) {
          this.setFrequentlyAddedPurchaseMode(card, 'subscribe');
          requestAnimationFrame(() => {
            planSel.focus();
            if (typeof planSel.showPicker === 'function') {
              try {
                planSel.showPicker();
              } catch (e) {
                /* unsupported */
              }
            }
          });
        }
        return;
      }
    }

    const addBtn = event.target.closest('[data-frequently-added-add]');
    if (addBtn && this.contains(addBtn)) {
      event.preventDefault();
      this.frequentlyAddedSubmitAdd(addBtn);
    }
  }

  onFrequentlyAddedChange(event) {
    if (event.target.closest('[data-complete-setup-card]')) return;
    const variantSel = event.target.closest('[data-variant-select]');
    if (variantSel && this.contains(variantSel)) {
      const card = variantSel.closest('[data-frequently-added-card]');
      if (card) this.frequentlyAddedApplyVariant(card, variantSel.value);
      return;
    }

    const planSel = event.target.closest('[data-selling-plan-select]');
    if (planSel && this.contains(planSel)) {
      const card = planSel.closest('[data-frequently-added-card]');
      if (card) this.setFrequentlyAddedPurchaseMode(card, 'subscribe');
    }
  }

  frequentlyAddedSubmitAdd(button) {
    const card = button.closest('[data-frequently-added-card]');
    if (!card || button.getAttribute('aria-disabled') === 'true') return;

    const variantSel = card.querySelector('[data-variant-select]');
    const variantId = parseInt(variantSel ? variantSel.value : this.frequentlyAddedParseJson(card)?.variants?.[0]?.id, 10);
    if (!variantId) return;

    const purchase = card.querySelector('[data-frequently-added-purchase]');
    const planSel = card.querySelector('[data-selling-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    const mode = purchaseVisible ? purchase.dataset.purchaseMode || 'onetime' : 'onetime';
    let sellingPlan = null;
    if (mode === 'subscribe' && planSel && !planSel.disabled && planSel.value) {
      sellingPlan = parseInt(planSel.value, 10);
    }

    const item = { id: variantId, quantity: 1 };
    if (sellingPlan) item.selling_plan = sellingPlan;

    button.setAttribute('aria-disabled', 'true');
    setDrawerAddButtonLoading(button, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;
    const bodyAdd = JSON.stringify({
      items: [item],
      sections: sections.join(','),
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd })
      .then((r) => r.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        const rowKey = card.dataset.rowKey;
        if (rowKey) {
          const removed = readFrequentlyAddedRemovedKeys();
          if (!removed.includes(rowKey)) {
            removed.push(rowKey);
            writeFrequentlyAddedRemovedKeys(removed);
          }
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'frequently-added',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setDrawerAddButtonLoading(button, false);
        button.removeAttribute('aria-disabled');
      });
  }

  completeSetupParseJson(card) {
    const el = card.querySelector('.js-complete-setup-json');
    if (!el) return null;
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return null;
    }
  }

  completeSetupResolveCard(el) {
    if (!el) return null;
    const card = el.closest('[data-complete-setup-card]');
    if (card) return card;
    const inner = el.closest('.drawer__complete-setup-card-inner');
    return inner?.closest('[data-complete-setup-card]') || null;
  }

  completeSetupResolveVariantId(checkbox, card) {
    const data = this.completeSetupParseJson(card);
    const variantSel = card.querySelector('[data-complete-setup-variant-select]');

    // Selector is the source of truth when present (checkbox data-* is only the initial SSR value).
    if (variantSel?.value) {
      const id = parseInt(variantSel.value, 10);
      if (checkbox) checkbox.dataset.completeSetupVariantId = String(id);
      return id;
    }

    const fromCheckbox = checkbox?.dataset?.completeSetupVariantId;
    if (fromCheckbox) return parseInt(fromCheckbox, 10);

    if (data?.defaultVariantId) return parseInt(String(data.defaultVariantId), 10);

    if (data?.variants?.[0]?.id) return parseInt(String(data.variants[0].id), 10);

    return NaN;
  }

  completeSetupIntervalLabel(plan, data) {
    const key = plan && plan.interval_suffix_key ? plan.interval_suffix_key : 'month';
    const i18n = data.i18n || {};
    return i18n[`interval_${key}`] || i18n.interval_month || 'month';
  }

  completeSetupUpdateBadges(card, mode) {
    const sub = card.querySelector('[data-complete-setup-badge-subscribe]');
    const one = card.querySelector('[data-complete-setup-badge-onetime]');
    if (!sub || !one) return;
    if (mode === 'subscribe') {
      sub.classList.remove('hidden');
      one.classList.add('hidden');
    } else {
      sub.classList.add('hidden');
      one.classList.remove('hidden');
    }
  }

  completeSetupUpdatePriceDisplay(card) {
    const data = this.completeSetupParseJson(card);
    if (!data) return;

    const variantSel = card.querySelector('[data-complete-setup-variant-select]');
    const variantId = variantSel ? variantSel.value : String(data.variants[0]?.id);
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const cb = card.querySelector('[data-complete-setup-checkbox]');
    if (cb) cb.dataset.completeSetupVariantId = String(variantId);

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    const mode = purchaseVisible ? purchase.dataset.purchaseMode || 'onetime' : 'onetime';

    let priceFormatted = v.price_formatted;
    const plans = v.selling_plans || [];
    let activePlan = null;
    if (mode === 'subscribe' && planSel && !planSel.disabled && plans.length > 0 && planSel.value) {
      activePlan = plans.find((p) => String(p.id) === String(planSel.value));
      if (activePlan && activePlan.price_formatted) {
        priceFormatted = activePlan.price_formatted;
      }
    }

    const priceEl = card.querySelector('[data-complete-setup-price]');
    const intervalEl = card.querySelector('[data-complete-setup-interval]');
    if (priceEl) priceEl.textContent = priceFormatted;
    if (intervalEl) {
      if (mode === 'subscribe' && activePlan) {
        const label = this.completeSetupIntervalLabel(activePlan, data);
        intervalEl.textContent = `/ ${label}`;
      } else {
        intervalEl.textContent = `/ ${data.i18n?.interval_once || 'once'}`;
      }
    }

    this.completeSetupUpdateBadges(card, mode);
  }

  setCompleteSetupPurchaseMode(card, mode) {
    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const onetimeBtn = card.querySelector('[data-complete-setup-purchase-type="onetime"]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    const wrap = card.querySelector('.drawer__complete-setup-subscribe-wrap');
    if (!purchase || !onetimeBtn || !planSel) {
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    purchase.dataset.purchaseMode = mode;

    if (mode === 'onetime') {
      onetimeBtn.classList.add('drawer__complete-setup-pill--active');
      onetimeBtn.setAttribute('aria-pressed', 'true');
      wrap?.classList.remove('drawer__complete-setup-subscribe-wrap--active');
      planSel.disabled = true;
    } else {
      onetimeBtn.classList.remove('drawer__complete-setup-pill--active');
      onetimeBtn.setAttribute('aria-pressed', 'false');
      wrap?.classList.add('drawer__complete-setup-subscribe-wrap--active');
      planSel.disabled = false;
    }

    this.completeSetupUpdatePriceDisplay(card);
  }

  completeSetupApplyVariant(card, variantId) {
    const data = this.completeSetupParseJson(card);
    if (!data) return;
    const v = data.variants.find((x) => String(x.id) === String(variantId));
    if (!v) return;

    const imgEl = card.querySelector('[data-complete-setup-img]');
    if (imgEl) {
      if (v.featured_media_url) {
        if (imgEl.tagName === 'IMG') {
          imgEl.src = v.featured_media_url;
          imgEl.classList.remove('hidden');
        }
      } else if (imgEl.tagName === 'IMG') {
        imgEl.removeAttribute('src');
        imgEl.classList.add('hidden');
      }
    }

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    if (!purchase || !planSel) {
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    const plans = v.selling_plans || [];
    if (plans.length === 0) {
      purchase.hidden = true;
      purchase.classList.add('hidden');
      const cb = card.querySelector('[data-complete-setup-checkbox]');
      if (cb) {
        cb.dataset.completeSetupVariantId = String(variantId);
        cb.disabled = !v.available;
      }
      this.completeSetupUpdatePriceDisplay(card);
      return;
    }

    purchase.hidden = false;
    purchase.classList.remove('hidden');
    planSel.innerHTML = '';
    plans.forEach((p) => {
      const o = document.createElement('option');
      o.value = String(p.id);
      o.textContent = p.name;
      planSel.appendChild(o);
    });

    let matchDefault = null;
    if (data.defaultSellingPlanId != null) {
      matchDefault = plans.find((p) => String(p.id) === String(data.defaultSellingPlanId));
    }
    const pick = matchDefault || plans[0];
    planSel.value = String(pick.id);

    if (matchDefault) {
      this.setCompleteSetupPurchaseMode(card, 'subscribe');
    } else {
      this.setCompleteSetupPurchaseMode(card, 'onetime');
    }

    const cb = card.querySelector('[data-complete-setup-checkbox]');
    if (cb) {
      cb.dataset.completeSetupVariantId = String(variantId);
      cb.disabled = !v.available;
    }

    this.completeSetupUpdatePriceDisplay(card);
  }

  onCompleteSetupFocusIn(event) {
    const planSel = event.target.closest('[data-complete-setup-plan-select]');
    if (!planSel || !this.contains(planSel)) return;
    const card = planSel.closest('[data-complete-setup-card]');
    if (!card) return;
    this.setCompleteSetupPurchaseMode(card, 'subscribe');
  }

  onCompleteSetupClick(event) {
    if (!event.target.closest('[data-complete-setup-card]')) return;

    const onetimeBtn = event.target.closest('[data-complete-setup-purchase-type="onetime"]');
    if (onetimeBtn && this.contains(onetimeBtn)) {
      const card = onetimeBtn.closest('[data-complete-setup-card]');
      if (card) this.setCompleteSetupPurchaseMode(card, 'onetime');
      return;
    }

    const subscribeWrap = event.target.closest('.drawer__complete-setup-subscribe-wrap');
    if (subscribeWrap && this.contains(subscribeWrap)) {
      const planSel = subscribeWrap.querySelector('[data-complete-setup-plan-select]');
      if (planSel?.disabled) {
        event.preventDefault();
        const card = subscribeWrap.closest('[data-complete-setup-card]');
        if (card) {
          this.setCompleteSetupPurchaseMode(card, 'subscribe');
          requestAnimationFrame(() => {
            planSel.focus();
            if (typeof planSel.showPicker === 'function') {
              try {
                planSel.showPicker();
              } catch (e) {
                /* unsupported */
              }
            }
          });
        }
      }
    }
  }

  onCompleteSetupChange(event) {
    const checkbox = event.target.closest('[data-complete-setup-checkbox]');
    if (checkbox && this.contains(checkbox)) {
      if (!checkbox.checked) return;
      this.completeSetupSubmitAdd(checkbox);
      return;
    }

    const variantSel = event.target.closest('[data-complete-setup-variant-select]');
    if (variantSel && this.contains(variantSel)) {
      const card = this.completeSetupResolveCard(variantSel);
      if (card) this.completeSetupApplyVariant(card, variantSel.value);
      return;
    }

    const planSel = event.target.closest('[data-complete-setup-plan-select]');
    if (planSel && this.contains(planSel)) {
      const card = planSel.closest('[data-complete-setup-card]');
      if (card) this.setCompleteSetupPurchaseMode(card, 'subscribe');
    }
  }

  completeSetupSubmitAdd(checkbox) {
    const card = this.completeSetupResolveCard(checkbox);
    if (!card || checkbox.getAttribute('aria-disabled') === 'true') return;

    const variantId = this.completeSetupResolveVariantId(checkbox, card);
    if (!variantId) {
      checkbox.checked = false;
      return;
    }

    const purchase = card.querySelector('[data-complete-setup-purchase]');
    const planSel = card.querySelector('[data-complete-setup-plan-select]');
    const purchaseVisible = purchase && !purchase.hasAttribute('hidden');
    const mode = purchaseVisible ? purchase.dataset.purchaseMode || 'onetime' : 'onetime';
    let sellingPlan = null;
    if (mode === 'subscribe' && planSel && !planSel.disabled && planSel.value) {
      sellingPlan = parseInt(planSel.value, 10);
    }

    const item = { id: variantId, quantity: 1 };
    if (sellingPlan) item.selling_plan = sellingPlan;

    checkbox.setAttribute('aria-disabled', 'true');
    checkbox.disabled = true;
    setCompleteSetupCheckLoading(checkbox, true);

    const sections = ['cart-drawer', 'cart-icon-bubble'];
    const sectionsUrl = window.location.pathname;
    const bodyAdd = JSON.stringify({
      items: [item],
      sections: sections.join(','),
      sections_url: sectionsUrl,
    });

    fetch(`${routes.cart_add_url}`, { ...fetchConfig(), body: bodyAdd })
      .then((r) => r.json())
      .then((addState) => {
        if (addState.status) {
          throw new Error(addState.description || addState.message || 'cart add');
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'complete-setup',
          cartData: addState,
          variantId: String(variantId),
        });

        this.renderContents(addState);
        this.classList.toggle('is-empty', addState.item_count === 0);
      })
      .catch((e) => {
        console.error(e);
        checkbox.checked = false;
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        setCompleteSetupCheckLoading(checkbox, false);
        checkbox.removeAttribute('aria-disabled');
        checkbox.disabled = false;
      });
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    this.purgeFrequentlyAddedFromStorage();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
